import { Outlet, Navigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';

function RadarAnimation() {
  return (
    <div className="relative w-80 h-80">
      {/* Outer rings */}
      <div className="absolute inset-0 rounded-full border border-accent/10" />
      <div className="absolute inset-[15%] rounded-full border border-accent/15" />
      <div className="absolute inset-[30%] rounded-full border border-accent/20" />
      <div className="absolute inset-[45%] rounded-full border border-accent/25" />

      {/* Sweep line — recolored from the legacy mint-teal to amber to
          match the operational accent palette. */}
      <div className="absolute inset-0 animate-radar-sweep">
        <div
          className="absolute top-1/2 left-1/2 w-1/2 h-[2px] origin-left"
          style={{
            background: 'linear-gradient(90deg, rgba(255, 181, 71, 0.8) 0%, transparent 100%)',
          }}
        />
      </div>

      {/* Sweep trail */}
      <div className="absolute inset-0 animate-radar-sweep" style={{ animationDelay: '-0.5s', opacity: 0.3 }}>
        <div
          className="absolute top-1/2 left-1/2 w-1/2 h-[2px] origin-left"
          style={{
            background: 'linear-gradient(90deg, rgba(255, 181, 71, 0.4) 0%, transparent 100%)',
          }}
        />
      </div>

      {/* Center dot */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-accent shadow-[0_0_12px_rgba(255,181,71,0.6)]" />

      {/* Blips */}
      <div className="absolute top-[25%] left-[60%] w-2 h-2 rounded-full bg-accent/80 animate-blip-pulse" />
      <div className="absolute top-[45%] left-[75%] w-1.5 h-1.5 rounded-full bg-accent/60 animate-blip-pulse" style={{ animationDelay: '-1s' }} />
      <div className="absolute top-[70%] left-[35%] w-2 h-2 rounded-full bg-sky/70 animate-blip-pulse" style={{ animationDelay: '-0.5s' }} />
      <div className="absolute top-[30%] left-[30%] w-1.5 h-1.5 rounded-full bg-alert/60 animate-blip-pulse" style={{ animationDelay: '-1.5s' }} />
      <div className="absolute top-[60%] left-[65%] w-1.5 h-1.5 rounded-full bg-accent/50 animate-blip-pulse" style={{ animationDelay: '-0.8s' }} />

      {/* Pulse rings */}
      <div className="absolute top-[25%] left-[60%] -translate-x-1/2 -translate-y-1/2 w-6 h-6 rounded-full border border-accent/30 animate-pulse-ring" />
      <div className="absolute top-[70%] left-[35%] -translate-x-1/2 -translate-y-1/2 w-6 h-6 rounded-full border border-sky/30 animate-pulse-ring" style={{ animationDelay: '-1s' }} />
    </div>
  );
}

export default function AuthLayout() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  if (isAuthenticated) {
    return <Navigate to="/map" replace />;
  }

  return (
    <div className="min-h-screen bg-navy-900 flex grid-overlay">
      {/* Left side — Radar visual */}
      <div className="hidden lg:flex lg:flex-1 flex-col items-center justify-center relative overflow-hidden">
        {/* Background gradient */}
        <div
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(ellipse at 50% 50%, rgba(255, 181, 71, 0.08) 0%, transparent 70%)',
          }}
        />

        <RadarAnimation />

        {/* Stats below radar */}
        <div className="mt-12 flex gap-8 relative z-10">
          <div className="text-center">
            <div className="text-2xl font-display font-bold text-accent">99.7%</div>
            <div className="text-xs text-gray-500 mt-1">Detection Rate</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-display font-bold text-sky">{'<'}200ms</div>
            <div className="text-xs text-gray-500 mt-1">Response Time</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-display font-bold text-accent">24/7</div>
            <div className="text-xs text-gray-500 mt-1">Monitoring</div>
          </div>
        </div>
      </div>

      {/* Right side — Login form */}
      <div className="flex flex-1 flex-col justify-center px-6 sm:px-12 lg:max-w-lg lg:px-16">
        {/* Brand lockup with C2 platform tag. Logo asset is the on-dark
            variant from the brand kit; the Signal Red C2 badge marks this
            as the command & control surface (vs. the org as a whole). */}
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-5">
            <img
              src="/brand/logo-on-dark.png"
              alt="Airspace Surveillance of New England"
              className="h-12 w-auto"
            />
            <span className="inline-flex items-center rounded-sm bg-brand-red px-2 py-0.5 font-brand text-[11px] font-bold tracking-[0.18em] text-white">
              C2
            </span>
          </div>
          <h1 className="font-heading text-3xl font-bold text-white">
            Welcome back
          </h1>
          <p className="mt-2 text-sm text-iron-300">
            Sign in to the Airspace Surveillance of New England command &amp; control platform
          </p>
        </div>

        {/* Form container */}
        <div className="glass-card p-8">
          <Outlet />
        </div>

        {/* Footer */}
        <p className="mt-8 text-xs text-gray-600 text-center">
          &copy; {new Date().getFullYear()} ASNE C2 · Airspace Surveillance of New England. All rights reserved.
        </p>
      </div>
    </div>
  );
}
